import { useLayoutEffect, useMemo, useRef } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  buildManusDestinationUrl,
  isManusDestinationSlug,
  type ManusDestinationSlug,
} from '@/constants/manusDestination'

/**
 * 嵌套 SPA 若在 iframe 内 pushState，会占用「联合会话历史」，浏览器后退会先退 iframe 再退父页。
 * 父页从 about:blank 用 location.replace 打开外链，避免多占一条初始记录；切换语言也用 replace。
 */
export default function DestinationEmbed() {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { slug = '' } = useParams<{ slug: string }>()
  const { i18n } = useTranslation()

  const safeSlug = isManusDestinationSlug(slug) ? (slug as ManusDestinationSlug) : null

  const targetUrl = useMemo(
    () => (safeSlug ? buildManusDestinationUrl(safeSlug, i18n.language) : ''),
    [safeSlug, i18n.language],
  )

  useLayoutEffect(() => {
    if (!targetUrl) return
    const el = iframeRef.current
    const win = el?.contentWindow
    if (!win) return
    try {
      win.location.replace(targetUrl)
    } catch {
      el.src = targetUrl
    }
  }, [targetUrl])

  if (!safeSlug) {
    return <Navigate to="/" replace />
  }

  return (
    <iframe
      ref={iframeRef}
      src="about:blank"
      className="block w-full border-0"
      style={{ minHeight: 'calc(100vh - 4.5rem)', height: 'calc(100dvh - 4.5rem)' }}
      title={safeSlug}
      allowFullScreen
    />
  )
}
